import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Copy, Crown, Loader2, RefreshCw, UserMinus, Users } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/AppHeader";
import { api, errMsg } from "@/lib/api";

const inviteUrl = (code) => `${window.location.origin}/join/${code}`;

const Members = ({ team, onRemove }) => (
  <ul className="divide-y divide-[var(--line)]/60" data-testid="team-members">
    {team.members.map((m) => (
      <li key={m.user_id} className="flex items-center gap-3 py-3" data-testid={`team-member-${m.user_id}`}>
        <div className="w-8 h-8 grid place-items-center bg-[var(--card)] border border-[var(--line)] font-mono text-xs">{(m.name || m.email)[0].toUpperCase()}</div>
        <div className="flex-1 min-w-0"><p className="text-sm truncate">{m.name}</p><p className="text-xs text-[var(--muted)] truncate">{m.email}</p></div>
        {m.is_owner ? <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[var(--amber)]"><Crown size={12} /> Owner</span>
          : team.is_owner && <button data-testid={`remove-member-${m.user_id}`} onClick={() => onRemove(m)} className="text-[var(--muted)] hover:text-red-400" title="Remove"><UserMinus size={15} /></button>}
      </li>
    ))}
  </ul>
);

const CreateTeam = ({ onDone }) => {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/team", { name }); toast.success("Team created"); onDone(); } catch (er) { toast.error(errMsg(er)); }
    setBusy(false);
  };
  return (
    <form onSubmit={create} className="border border-[var(--line)] bg-[var(--surface)] p-6 max-w-xl" data-testid="create-team-form">
      <h3 className="font-display font-semibold">Start a team</h3>
      <p className="mt-2 text-sm text-[var(--muted)]">Up to 5 people share every draft, Rhino Live and your Design DNA. Each person keeps their own card on file and pays for the drafts they create.</p>
      <div className="mt-5 flex gap-2">
        <input data-testid="team-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Studio name"
          className="flex-1 min-w-0 bg-[var(--card)] border border-[var(--line)] focus:border-[var(--cyan)] outline-none px-3 py-2.5 text-sm transition-colors" />
        <button data-testid="create-team-btn" disabled={busy} className="px-5 bg-[var(--cyan)] text-[var(--bg)] font-semibold text-sm disabled:opacity-50">{busy ? <Loader2 size={15} className="animate-spin" /> : "Create"}</button>
      </div>
    </form>
  );
};

export default function Team() {
  const [team, setTeam] = useState(undefined);
  const navigate = useNavigate();
  const load = useCallback(() => api.get("/team").then(({ data }) => setTeam(data.team)), []);
  useEffect(() => { document.title = "Team · RhinoDraft AI"; load(); }, [load]);
  const run = async (fn, msg, after) => { try { await fn(); toast.success(msg); after ? after() : load(); } catch (e) { toast.error(errMsg(e)); } };
  const copy = async () => { await navigator.clipboard.writeText(inviteUrl(team.invite_code)); toast.success("Invite link copied"); };

  return (
    <div className="min-h-screen blueprint-grid">
      <AppHeader />
      <main className="max-w-4xl mx-auto px-6 py-14">
        <p className="rise font-mono text-xs uppercase tracking-[0.3em] text-[var(--cyan)]">Team seats</p>
        <h1 className="rise mt-3 text-4xl sm:text-5xl font-bold tracking-tight">{team ? team.name : "Your firm"}</h1>
        <div className="rise d2 mt-10">
          {team === undefined ? <Loader2 className="animate-spin text-[var(--cyan)]" /> : !team ? <CreateTeam onDone={load} /> : (
            <div className="grid lg:grid-cols-[1.3fr_1fr] gap-8">
              <div className="border border-[var(--line)] bg-[var(--surface)] p-6 min-w-0">
                <div className="flex items-center justify-between"><h3 className="font-display font-semibold flex items-center gap-2"><Users size={16} className="text-[var(--cyan)]" /> Members</h3>
                  <span className="font-mono text-xs text-[var(--muted)]" data-testid="team-seats">{team.seats_used} / {team.max_seats} seats</span></div>
                <div className="mt-3"><Members team={team} onRemove={(m) => window.confirm(`Remove ${m.name} from the team?`) && run(() => api.delete(`/team/members/${m.user_id}`), "Member removed")} /></div>
              </div>
              <div className="space-y-4 min-w-0">
                {team.is_owner ? (
                  <div className="border border-[var(--line)] p-6" data-testid="invite-panel">
                    <h3 className="font-display font-semibold">Invite link</h3>
                    <p className="mt-2 text-xs text-[var(--muted)]">Anyone who signs in with this link joins your team (until seats run out). They'll put their own card on file to pay for their drafts.</p>
                    <div className="mt-4 flex gap-2">
                      <input readOnly value={inviteUrl(team.invite_code)} data-testid="invite-link-input" className="flex-1 min-w-0 bg-[var(--card)] border border-[var(--line)] px-3 py-2 text-xs font-mono" />
                      <button data-testid="copy-invite-btn" onClick={copy} className="px-3 border border-[var(--line)] hover:border-[var(--cyan)]"><Copy size={14} /></button>
                    </div>
                    <button data-testid="rotate-invite-btn" onClick={() => run(() => api.post("/team/invite/rotate"), "New link created — the old one no longer works")}
                      className="mt-3 flex items-center gap-1.5 text-xs text-[var(--muted)] hover:text-white"><RefreshCw size={12} /> Reset link</button>
                  </div>
                ) : null}
                <div className="border border-[var(--line)] p-6 text-sm text-[var(--muted)]">
                  <p>Shared with the team: every draft, Rhino Live and Design DNA. Each person pays for the drafts they start, at $1 per sq ft.</p>
                  {team.is_owner
                    ? <button data-testid="disband-team-btn" onClick={() => window.confirm("Disband the team? Members keep their own drafts but stop seeing the shared ones.") && run(() => api.delete("/team"), "Team disbanded")} className="mt-4 text-xs text-red-400 hover:underline">Disband team</button>
                    : <button data-testid="leave-team-btn" onClick={() => window.confirm("Leave this team? You'll keep the drafts you created.") && run(() => api.post("/team/leave"), "You left the team", () => navigate("/dashboard"))} className="mt-4 text-xs text-red-400 hover:underline">Leave team</button>}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export function JoinTeam() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    localStorage.removeItem("rd_next");
    api.get(`/team/invite/${code}`).then(({ data }) => setInfo(data)).catch((e) => setErr(errMsg(e)));
  }, [code]);
  const join = async () => {
    setBusy(true);
    try { await api.post("/team/join", { code }); toast.success(`Joined ${info.name}`); navigate("/dashboard"); } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="min-h-screen blueprint-grid">
      <AppHeader />
      <main className="max-w-lg mx-auto px-6 py-24 text-center rise" data-testid="join-team-page">
        {err ? <p className="text-red-400" data-testid="join-error">{err}</p> : !info ? <Loader2 className="mx-auto animate-spin text-[var(--cyan)]" /> : (
          <>
            <Users size={36} className="mx-auto text-[var(--cyan)]" />
            <h1 className="mt-5 text-3xl font-bold">Join {info.name}</h1>
            <p className="mt-2 text-[var(--muted)]">{info.owner_name} invited you · {info.seats_used}/{info.max_seats} seats used</p>
            {info.already_member ? <button onClick={() => navigate("/dashboard")} className="mt-8 text-[var(--cyan)] hover:underline">You're already on this team — open the studio</button>
              : info.on_other_team ? <p className="mt-8 text-sm text-[var(--amber)]">Leave your current team first (Team page), then open this link again.</p>
                : <button data-testid="join-team-btn" onClick={join} disabled={busy} className="mt-8 px-8 py-3 bg-[var(--cyan)] text-[var(--bg)] font-semibold disabled:opacity-50">{busy ? "Joining…" : "Join team"}</button>}
          </>
        )}
      </main>
    </div>
  );
}

export const JoinGate = ({ children }) => {
  const { code } = useParams();
  localStorage.setItem("rd_next", `/join/${code}`);
  return children;
};
